import type { AiCapabilityPage } from "../schema.js";
import { LIVE, MANAGER_ROLES } from "./_shared.js";

/**
 * `REPORT-VIEW` (@UC0216) — trang **redirect** (US-AIV2-0049): chat KHÔNG hỏi ô, KHÔNG có nút Đồng ý,
 * chỉ một câu giải thích + nút mở đúng màn hình `/groups/:groupId/settings` (mục xuất dữ liệu) (ADR-0009 bất biến 5).
 * Cửa mobile trang này chịu trách nhiệm (trang chat T1 `REPORT-EXPORT` là task 0057 (QĐ PO 17/09 #3) — khi trang đó lên, cửa xuất file chuyển sang nó).

 * 🔴 Vai HẸP HƠN cảm giác ban đầu (US-AIV2-0049, review #827 H1 — ĐO từ guard thật, đừng nới lại):
 *   cả 5 cửa báo cáo gác OWNER/ADMIN/TREASURER — member thường mở màn báo cáo là 403 ở MỌI cửa.
 *   Trang redirect chỉ mở màn hình, nên mời sai vai = dẫn người ta tới nút bấm vào là 403
 *   (CLAUDE.md §4 cấm: "dẫn member thường vào form quản trị rồi để 403 ở bước chốt").
 *   Khoá bằng `roles-parity.invariants.spec.ts` — nới ở đây mà GUI không nới thì test ĐỎ.
 * ⚠️ US-E23.F1-0867 (QĐ PO 30/09/2026): BA cửa CẤP NHÓM (`POST …/reports/export` kind `finance` · `GET …/reports[/:id]` — chỉ bản
 *   của chính mình) nay MỞ cho member thường; hai cửa CẤP QUỸ vẫn O/A/T.
 * 🔴 GUIDE-SCREEN-TRUTH (lô review 15/10 G-1 · uỷ quyền 09/10 "không phình scope, logic đúng"):
 *   · Đích đổi từ `/groups/:groupId/reports` (CHỈ danh sách bản đã xuất — không "xem theo kỳ", không tạo được) sang Cài đặt nhóm: nơi
 *     «Tạo báo cáo thu chi» (PDF, máy tự tải khi xong) VÀ «Xem bản đã xuất» cùng nằm. Câu cũ "xong mình sẽ báo bạn" sai: tin «xong» đã
 *     gỡ (US-E23.F1-0799), chỉ tin HỎNG còn.
 *   · Vai: cổng THẬT của GUI là MỌI vai (cờ `canExportFinanceReport`; đo B0: member `tu` POST `…/reports/export` kind `finance` ⇒ 202).
 *     NHƯNG mở trang cho member thì khối prompt của member mang `llm.description` của trang này — mô tả nhắc tên `REPORT-EXPORT` (trang GHI
 *     chỉ-quản-lý) ⇒ đỏ `toolset-role-leak.invariants.spec.ts` (@UC0152). Gỡ chữ đó = đổi `llm.*` ⇒ ca eval cũ đi + cần lượt TPM — ngoài
 *     phạm vi G-1 ⇒ GIỮ `MANAGER_ROLES` (hẹp hơn GUI: member không nghe 403, chỉ chưa được mời qua chat). NỢ có địa chỉ: PR G-1.
 */
export const REPORT_VIEW = {
  id: "REPORT-VIEW",
  uc: "@UC0216",
  tier: "redirect",
  status: LIVE,
  roles: MANAGER_ROLES,
  flags: [],
  slots: [],
  llm: {
    description:
      "MỞ MÀN HÌNH báo cáo của nhóm hoặc của một quỹ để XEM BẰNG MẮT theo kỳ. Người dùng nói 'xem/mở/coi báo cáo' thì vào đây. Nếu câu đòi một FILE để tải về hay gửi đi ('xuất file', 'tải excel', 'export', 'download sổ thu chi') thì đó là REPORT-EXPORT, không phải trang này.",
    examples: [
      "mở báo cáo của nhóm ra coi",
      "xem lại báo cáo quỹ tháng trước",
      "xem báo cáo nhóm",
      "xem bao cao nhom",
      "coi báo cáo của quỹ trên app",
      "coi bao cao cua quy tren app",
      "báo cáo tháng này",
      "báo cáo",
      // ——— 0105: thêm cho đủ 40 ———
      "bao cao",
      "bao cao thang nay",
      "mo bao cao cua nhom ra coi",
      "xem lai bao cao quy thang truoc",
      "mở màn hình báo cáo",
      "mo man hinh bao cao",
      "vào trang báo cáo của nhóm",
      "vao trang bao cao cua nhom",
      "xem báo cáo thu chi của nhóm trên màn hình",
      "xem bao cao thu chi cua nhom tren man hinh",
      "báo cáo quý này",
      "bao cao quy nay",
      "xem số liệu báo cáo của quỹ",
      "xem so lieu bao cao cua quy",
      "cho mình coi tình hình thu chi theo báo cáo",
      "cho minh coi tinh hinh thu chi theo bao cao",
      "mở bảng thu chi theo kỳ của nhóm",
      "mo bang thu chi theo ky cua nhom",
      "báo cáo của quỹ lớp",
      "bao cao cua quy lop",
      "xem báo cáo theo kỳ",
      "xem bao cao theo ky",
      "làm báo cáo cuối năm cho nhóm",
      "lam bao cao cuoi nam cho nhom",
      "xem bảng báo cáo tổng hợp",
      "xem bang bao cao tong hop",
      "báo cáo thu chi nhóm ở đâu?",
      "bao cao thu chi nhom o dau",
      "màn hình báo cáo nhóm",
      "man hinh bao cao nhom",
      "cho tôi coi báo cáo quỹ lớp",
      "cho toi coi bao cao quy lop",
    ],
  },
  screen: "/groups/:groupId/settings",
  screenShows: "report_export",
  endpoints: [
    "GET /groups/{groupId}/reports",
    "GET /groups/{groupId}/reports/{reportId}",
    "GET /funds/{fundId}/reports",
    "GET /funds/{fundId}/reports/{reportId}",
    "POST /groups/{groupId}/reports/export",
  ],
  facts: [],
  templates: {
    redirect:
      "Mình mở cài đặt nhóm nhé. Ở mục «Dữ liệu & Xuất dữ liệu», chọn «Tạo báo cáo thu chi» để làm file PDF — xong máy tự tải về, bạn rời màn cũng được. Các bản đã tạo xem lại ở «Xem bản đã xuất».",
  },
  journey: { after: ["READ-FUND-MONEY", "SHARE-TRANSPARENCY"] },
  help: { title: "Báo cáo nhóm", tryNow: "xem báo cáo nhóm" },
} satisfies AiCapabilityPage;
